import { useMemo, useState } from 'react'
import clsx from 'clsx'
import { publicConfig } from 'config'
import { ownStanding, sentCount, tierLabel } from 'lib/community/model'
import { rankCard } from 'lib/community/hub'
import { pointsGuideRows, type BadgeFacts, type PointsGuide } from 'lib/community/gamification'
import { freshState, nextBadge, type Voice } from 'lib/community/stats'
import type { CommunityMemberResponse, CommunityStanding } from 'lib/community/types'
import type { OwnerCommunityMemberStats } from 'lib/dealhunt/types'
import BadgeHex from './BadgeHex'
import OwnPicker from './OwnPicker'
import { useOwnMemberMe } from './useOwnMemberMe'
import {
  GearVsOwnersCard,
  MonthLedger,
  PointsRankCard,
  ReadersByReviewCard,
  ReadersCard,
  ReadingTimeCard,
} from './MemberStatsCards'
import base from './community.module.css'
import styles from './member-stats.module.css'

/**
 * The member hub's Stats tab body (Member Stats design boards 1a–1i; contract 2026-10-11).
 *
 * Stats are PUBLIC: the server HTML (shared-cached, the same for every reader) draws every figure
 * in the third person — the visitor page (Constraint 35cf845b). Once `/me` says the page is the
 * reader's own (`useOwnMemberMe`, null under "View as visitor"), the same figures switch to the
 * second person and the coaching appears: the CTA, Next badge, the rank hint, the photo note.
 * Nothing is fetched here; the payload is the page's `getServerSideProps` read.
 */

type Stats = OwnerCommunityMemberStats

export interface MemberStatsViewProps {
  member: CommunityMemberResponse
  /** null = DealHunt has no stats to give yet (503 before the db:push, a blip). */
  stats: Stats | null
  guide: PointsGuide | null
  facts: BadgeFacts
  /** `/summary.last_ranked_points.all` — the fallback for the fresh board's last-place line. */
  lastRankedPoints: number | null
  /** The member's own view (after hydration, not previewing as a visitor). */
  own: boolean
  /** The member's own standing from `/me` (Next badge's pinned head); null for a visitor. */
  standing: CommunityStanding | null
}

/** "Stats aren't available yet" — DealHunt answered 503 / 404 / nothing: the tab, never a 500. */
export const StatsUnavailable: React.VFC = () => (
  <section className={clsx(styles.card, styles.unavailable)} role="status">
    <h2 className={styles.title}>{'Stats aren’t available yet'}</h2>
    <p className={styles.sentence}>
      {'Readers, time spent reading and points by month will show here soon.'}
    </p>
  </section>
)

/** The own CTA: "Leave an owner review →", the owner picker in place (as Gear's). */
const LeaveReview: React.VFC = () => {
  const [picking, setPicking] = useState(false)
  return picking ? (
    <div className={styles.picker}>
      <OwnPicker id="stats-own-results" autoFocus />
    </div>
  ) : (
    <button type="button" className={base.btn} onClick={() => setPicking(true)}>
      {'Leave an owner review →'}
    </button>
  )
}

/** Board 1a: no counted review yet — expected readers, what earns points, the next badge, the
 *  board, and the running month, instead of empty charts. */
export const FreshStats: React.VFC<MemberStatsViewProps & { stats: Stats; voice: Voice }> = ({
  member,
  stats,
  guide,
  facts,
  lastRankedPoints,
  standing,
  voice,
}) => {
  const fresh = freshState(stats, voice)
  const earn = guide ? pointsGuideRows(guide) : []
  const badge = voice.own ? nextBadge(member.badges, standing, facts.caps) : null
  const last = sentCount(stats.points.last_ranked_points) ?? lastRankedPoints
  // "50 = Contributor": DealHunt's figures (points + points_to_go), never a threshold held here.
  const next = stats.points.next_tier
  const nextTier = next
    ? `${(stats.points.total + next.points_to_go).toLocaleString('en-US')} = ${tierLabel(next.id)}`
    : null
  const rank = rankCard(
    { rank_all: stats.points.rank_all, rank_month: stats.points.rank_month },
    last,
    voice.own
  )
  return (
    <div className={styles.stack}>
      <section className={styles.freshPanel}>
        <div className={styles.freshText}>
          <h2 className={clsx(styles.eyebrow, styles.blue)}>{'Readers'}</h2>
          <p className={styles.title}>{'No readers yet'}</p>
          <p className={styles.sentence}>{fresh?.sentence}</p>
        </div>
        {voice.own && <LeaveReview />}
      </section>
      <div className={styles.grid}>
        {earn.length > 0 && (
          <section className={styles.card}>
            <div className={styles.cardHead}>
              <h2 className={styles.eyebrow}>{'What earns points'}</h2>
              {nextTier && <span className={styles.cardHeadNote}>{nextTier}</span>}
            </div>
            <ul className={styles.list}>
              {earn.map(row => (
                <li key={row.key} className={styles.earnRow}>
                  <span>{row.label}</span>
                  <b className={styles.blue}>{row.value}</b>
                </li>
              ))}
            </ul>
          </section>
        )}
        <div className={styles.stack}>
          {badge && (
            <section className={styles.card}>
              <h2 className={styles.eyebrow}>{'Next badge'}</h2>
              <div className={styles.badgeRow}>
                <BadgeHex badge={badge.badge} className={styles.badgeHex} />
                <div className={styles.reviewText}>
                  <span className={styles.badgeName}>{badge.name}</span>
                  {badge.line && <span className={styles.muted}>{badge.line}</span>}
                </div>
              </div>
            </section>
          )}
          <section className={styles.card}>
            <h2 className={styles.eyebrow}>{'Leaderboard'}</h2>
            <p className={styles.sentence}>
              {[rank.allTime === 'Not ranked yet' ? 'Not ranked yet.' : `${rank.allTime} all time.`]
                .concat(rank.note ? [rank.note] : [])
                .concat(rank.lastPlace ? [`${rank.lastPlace}.`] : [])
                .join(' ')}
            </p>
          </section>
        </div>
      </div>
      <MonthLedger stats={stats} voice={voice} joinedAt={member.joined_at} />
    </div>
  )
}

/** The read ids' names per review, from the public member payload's feed items (the stats
 *  payload sends ids only). */
function readLabels(member: CommunityMemberResponse): Map<string, Map<string, string>> {
  const out = new Map<string, Map<string, string>>()
  ;(member.reviews ?? []).forEach(item => {
    const names = new Map<string, string>()
    ;(item.labels?.reads ?? []).forEach(r => {
      if (r.label) {
        names.set(r.id, r.label)
      }
    })
    out.set(item.note.id, names)
  })
  return out
}

/** The tab body for a given `own` — pure, so the SSR tests render both people. */
export const MemberStatsView: React.VFC<MemberStatsViewProps> = props => {
  const { member, stats, guide, own } = props
  const labels = useMemo(() => readLabels(member), [member])
  if (!stats) {
    return <StatsUnavailable />
  }
  const voice: Voice = { own, handle: member.handle }
  if (stats.reviews.length === 0) {
    return <FreshStats {...props} stats={stats} voice={voice} />
  }
  const trailingSlash = publicConfig('useTrailingSlash')
  const points = guide?.points ?? null
  return (
    <div className={styles.stack}>
      <ReadersCard stats={stats} voice={voice} />
      <div className={styles.grid}>
        <ReadingTimeCard stats={stats} voice={voice} />
        <PointsRankCard stats={stats} voice={voice} replyPoints={sentCount(points?.reply)} />
      </div>
      <ReadersByReviewCard
        stats={stats}
        voice={voice}
        trailingSlash={trailingSlash}
        photoPoints={sentCount(points?.photo_verified)}
      />
      <GearVsOwnersCard stats={stats} voice={voice} labels={labels} />
      <MonthLedger stats={stats} voice={voice} joinedAt={member.joined_at} />
    </div>
  )
}

/** The Stats tab with its hydration gate: the visitor view until `/me` says it is the member. */
export const MemberStats: React.VFC<Omit<MemberStatsViewProps, 'own' | 'standing'>> = props => {
  const me = useOwnMemberMe(props.member.handle)
  return (
    <MemberStatsView {...props} own={me !== null} standing={ownStanding(me, props.member.handle)} />
  )
}
